import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { Icon, type IconName } from './Icon.tsx'

function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ')
}

function hasContent(node: ReactNode): boolean {
  return node !== undefined && node !== null && node !== false && node !== ''
}

type Tone = 'fg' | 'muted' | 'error'

/* ----------------------------------------------------------------- tlacitka */

export type ButtonKind = 'primary' | 'outlined' | 'danger' | 'text' | 'quiet' | 'link'

export function Button({
  kind = 'outlined',
  icon,
  full = false,
  compact = false,
  className,
  type = 'button',
  children,
  ...rest
}: {
  kind?: ButtonKind | undefined
  icon?: IconName | undefined
  full?: boolean | undefined
  compact?: boolean | undefined
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      type={type}
      className={cx('btn', `btn-${kind}`, full && 'btn-full', compact && 'btn-compact', className)}
    >
      {icon && <Icon name={icon} />}
      {hasContent(children) && <span className="btn-label">{children}</span>}
    </button>
  )
}

export function IconButton({
  icon,
  label,
  bordered = false,
  tone = 'fg',
  iconSize = 22,
  className,
  type = 'button',
  title,
  ...rest
}: {
  icon: IconName
  label: string
  bordered?: boolean | undefined
  tone?: Tone | undefined
  iconSize?: number | undefined
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>) {
  return (
    <button
      {...rest}
      type={type}
      aria-label={label}
      title={title ?? label}
      className={cx('icon-btn', bordered && 'icon-btn-bordered', `tone-${tone}`, className)}
    >
      <Icon name={icon} size={iconSize} />
    </button>
  )
}

/* --------------------------------------------------------------------- pole */

export function TextField({
  label,
  hint,
  error,
  trailing,
  trailingIsButton = false,
  className,
  inputClassName,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  ...rest
}: {
  label?: string | undefined
  hint?: string | undefined
  error?: string | null | undefined
  trailing?: ReactNode
  trailingIsButton?: boolean | undefined
  inputClassName?: string | undefined
} & InputHTMLAttributes<HTMLInputElement>) {
  const autoId = useId()
  const inputId = id ?? autoId
  const hintId = `${inputId}-hint`
  const errorId = `${inputId}-error`
  const hasTrailing = hasContent(trailing)
  const describedBy = cx(ariaDescribedBy, error && errorId, hint && hintId) || undefined

  return (
    <div className={cx('field', className)}>
      {label && (
        <label className="field-label" htmlFor={inputId}>
          {label}
        </label>
      )}
      <div
        className={cx(
          'field-box',
          hasTrailing && 'has-trailing',
          hasTrailing && trailingIsButton && 'trailing-button',
          error && 'is-invalid',
        )}
      >
        <input
          {...rest}
          id={inputId}
          className={cx('field-input', inputClassName)}
          aria-describedby={describedBy}
          aria-invalid={error ? true : ariaInvalid}
        />
        {hasTrailing && <span className="field-trailing">{trailing}</span>}
      </div>
      {error && (
        <span id={errorId} className="field-error">
          {error}
        </span>
      )}
      {hint && (
        <span id={hintId} className="field-hint">
          {hint}
        </span>
      )}
    </div>
  )
}

export interface SelectOption<T extends string> {
  value: T
  label: string
  /** Druhy radek v menu (mesto a mena obchodu). */
  sub?: string | undefined
  /** Kratsi text do uzkeho tlacitka („Biedronka" misto celeho nazvu). */
  triggerLabel?: string | undefined
}

interface MenuPosition {
  top?: number
  bottom?: number
  left: number
  width: number
  maxHeight: number
}

const MENU_MIN_WIDTH = 208
const MENU_MAX_HEIGHT = 320

/** Bez diakritiky a velkych pismen — pro hledani ve vyberu. */
function plainText(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase('cs').trim()
}

/**
 * Vlastni vyber misto nativniho <select>. Nativni menu nejde nastylovat
 * (modre zvyrazneni, systemove pismo, v tmavem motivu nesedi) a ve spodnim
 * panelu by ho orizl scroll kontejner — proto menu v portalu s pevnou pozici.
 * Vzor „select-only combobox" z WAI-ARIA APG: fokus zustava na tlacitku,
 * aktivni volba jde pres aria-activedescendant.
 */
export function SelectField<T extends string>({
  label,
  hint,
  value,
  options,
  onChange,
  ariaLabel,
  placeholder = 'Vyber',
  className,
  searchPlaceholder,
}: {
  label?: string | undefined
  hint?: string | undefined
  value: T
  options: readonly SelectOption<NoInfer<T>>[]
  onChange(value: NoInfer<T>): void
  /** Popis pro ctecku, kdyz pole nema viditelny label. */
  ariaLabel?: string | undefined
  placeholder?: string | undefined
  className?: string | undefined
  /** Dlouhy seznam (prodejny Kauflandu): v menu je pole pro hledani v nazvu a druhem radku. */
  searchPlaceholder?: string | undefined
}) {
  const id = useId()
  const listId = `${id}-menu`
  const labelId = `${id}-label`
  const valueId = `${id}-value`
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const [query, setQuery] = useState('')
  const [position, setPosition] = useState<MenuPosition | null>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const searchable = searchPlaceholder !== undefined

  const selectedIndex = options.findIndex((o) => o.value === value)
  const selected = options[selectedIndex]
  const wanted = plainText(query)
  // „podebr" najde Poděbrady, „290" PSC.
  const shown =
    searchable && wanted
      ? options.filter((o) => plainText(`${o.label} ${o.sub ?? ''}`).includes(wanted))
      : options

  function place() {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const width = Math.max(rect.width, MENU_MIN_WIDTH)
    let left = rect.left
    if (left + width > window.innerWidth - 8) left = rect.right - width
    left = Math.max(8, left)
    const below = window.innerHeight - rect.bottom - 12
    const above = rect.top - 12
    const height = Math.min(MENU_MAX_HEIGHT, options.length * 48 + 10 + (searchable ? 52 : 0))
    // Dolu, pokud se menu vejde nebo je dole aspon tolik mista jako nahore.
    if (below >= height || below >= above) {
      setPosition({ top: rect.bottom + 4, left, width, maxHeight: Math.min(MENU_MAX_HEIGHT, below) })
    } else {
      setPosition({ bottom: window.innerHeight - rect.top + 4, left, width, maxHeight: Math.min(MENU_MAX_HEIGHT, above) })
    }
  }

  function openMenu() {
    place()
    setQuery('')
    setActive(Math.max(0, selectedIndex))
    setOpen(true)
  }

  function close(returnFocus = true) {
    setOpen(false)
    if (returnFocus) triggerRef.current?.focus()
  }

  function choose(index: number) {
    const option = shown[index]
    if (!option) return
    onChange(option.value)
    close()
  }

  // S hledanim jde fokus do pole v menu, sipky a Enter ovlada to pole.
  useEffect(() => {
    if (open && searchable) searchRef.current?.focus({ preventScroll: true })
  }, [open, searchable])

  function onSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    const last = shown.length - 1
    switch (event.key) {
      case 'ArrowDown':
        setActive((i) => Math.min(last, i + 1))
        break
      case 'ArrowUp':
        setActive((i) => Math.max(0, i - 1))
        break
      case 'Enter':
        choose(active)
        break
      case 'Escape':
        event.stopPropagation()
        close()
        break
      case 'Tab':
        close(false)
        return
      default:
        return
    }
    event.preventDefault()
  }

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return
      close(false)
    }
    // Menu ma pevnou pozici; pri scrollu stranky nebo panelu jde s tlacitkem.
    const onScroll = (event: Event) => {
      if (menuRef.current?.contains(event.target as Node)) return
      place()
    }
    document.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', place)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', place)
    }
    // place a close ctou jen refy a aktualni options — staci pri otevreni.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (open) document.getElementById(`${id}-option-${active}`)?.scrollIntoView({ block: 'nearest' })
  }, [open, active, id])

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault()
        openMenu()
      }
      return
    }
    const last = options.length - 1
    switch (event.key) {
      case 'ArrowDown':
        setActive((i) => Math.min(last, i + 1))
        break
      case 'ArrowUp':
        setActive((i) => Math.max(0, i - 1))
        break
      case 'Home':
        setActive(0)
        break
      case 'End':
        setActive(last)
        break
      case 'Enter':
      case ' ':
        choose(active)
        break
      case 'Escape':
        // Jinak by Escape zavrel i spodni panel, ve kterem vyber lezi.
        event.stopPropagation()
        close()
        break
      case 'Tab':
        close(false)
        return
      default: {
        if (event.key.length !== 1) return
        const letter = event.key.toLocaleLowerCase('cs')
        const order = [...options.keys()].map((k) => (active + 1 + k) % options.length)
        const match = order.find((i) => options[i]?.label.toLocaleLowerCase('cs').startsWith(letter))
        if (match !== undefined) setActive(match)
      }
    }
    event.preventDefault()
  }

  return (
    <div className={cx('field', className)}>
      {label && (
        <span className="field-label" id={labelId}>
          {label}
        </span>
      )}
      <div
        ref={triggerRef}
        role="combobox"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open ? `${id}-option-${active}` : undefined}
        aria-labelledby={label ? `${labelId} ${valueId}` : undefined}
        aria-label={label ? undefined : ariaLabel}
        className={cx('field-box', 'select-trigger', open && 'is-open')}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onKeyDown}
      >
        <span id={valueId} className={cx('select-value', !selected && 'is-placeholder')}>
          {selected ? (selected.triggerLabel ?? selected.label) : placeholder}
        </span>
        <Icon name={open ? 'chevron_up' : 'chevron_down'} size={18} className="select-trigger-icon" />
      </div>
      {hint && <span className="field-hint">{hint}</span>}
      {open &&
        position &&
        createPortal(
          <div
            ref={menuRef}
            id={listId}
            role="listbox"
            aria-label={label ?? ariaLabel}
            className={cx('select-menu', position.bottom !== undefined && 'opens-up')}
            style={position}
          >
            {searchable && (
              <input
                ref={searchRef}
                type="search"
                className="select-search"
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                aria-controls={listId}
                aria-activedescendant={shown.length > 0 ? `${id}-option-${active}` : undefined}
                value={query}
                autoComplete="off"
                onChange={(e) => {
                  setQuery(e.target.value)
                  setActive(0)
                }}
                onKeyDown={onSearchKeyDown}
              />
            )}
            {searchable && shown.length === 0 && <div className="select-empty">Nic takového tu není</div>}
            {shown.map((option, index) => {
              const isSelected = option.value === value
              return (
                <div
                  key={option.value}
                  id={`${id}-option-${index}`}
                  role="option"
                  aria-selected={isSelected}
                  className={cx('select-option', index === active && 'is-active', isSelected && 'is-selected')}
                  onPointerMove={() => setActive(index)}
                  onClick={() => choose(index)}
                >
                  <span className="select-option-text">
                    <span className="select-option-label">{option.label}</span>
                    {option.sub && <span className="select-option-sub">{option.sub}</span>}
                  </span>
                  {isSelected && <Icon name="check" size={18} strokeWidth={2.2} />}
                </div>
              )
            })}
          </div>,
          document.body,
        )}
    </div>
  )
}

/* -------------------------------------------------- stepper, segment, volby */

export function Stepper({
  value,
  onDecrement,
  onIncrement,
  decrementLabel = 'Méně',
  incrementLabel = 'Více',
  disabled = false,
  removes = false,
  className,
}: {
  value: string
  onDecrement(): void
  onIncrement(): void
  decrementLabel?: string | undefined
  incrementLabel?: string | undefined
  disabled?: boolean | undefined
  /** Minus by polozku odebral — ukaze se kos, at to neni prekvapeni. */
  removes?: boolean | undefined
  className?: string | undefined
}) {
  return (
    <div className={cx('stepper', className)}>
      <button
        type="button"
        className={cx('stepper-btn', removes && 'is-remove')}
        aria-label={decrementLabel}
        onClick={onDecrement}
        disabled={disabled}
      >
        <Icon name={removes ? 'trash' : 'minus'} />
      </button>
      <span className="stepper-value" aria-live="polite">
        {value}
      </span>
      <button
        type="button"
        className="stepper-btn"
        aria-label={incrementLabel}
        onClick={onIncrement}
        disabled={disabled}
      >
        <Icon name="plus" />
      </button>
    </div>
  )
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: {
  // T se odvozuje jen z `value`; jinak by literaly v options zuzily T na string
  // a onChange={setState} by neprosel typovou kontrolou.
  options: readonly { value: NoInfer<T>; label: string }[]
  value: T
  onChange(value: NoInfer<T>): void
  label: string
  className?: string | undefined
}) {
  const cells = useRef<(HTMLButtonElement | null)[]>([])
  const activeIndex = options.findIndex((o) => o.value === value)

  // Radiova skupina: sipky meni vyber, Tab skace jen na vybranou bunku.
  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const count = options.length
    if (count === 0) return
    const current = Math.max(0, activeIndex)
    let next: number
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (current - 1 + count) % count
        break
      case 'ArrowRight':
      case 'ArrowDown':
        next = (current + 1) % count
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = count - 1
        break
      default:
        return
    }
    event.preventDefault()
    const option = options[next]
    if (!option) return
    onChange(option.value)
    cells.current[next]?.focus()
  }

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cx('segmented', className)}
      onKeyDown={onKeyDown}
    >
      {options.map((option, index) => {
        const checked = option.value === value
        const focusable = checked || (activeIndex === -1 && index === 0)
        return (
          <button
            key={option.value}
            ref={(el) => {
              cells.current[index] = el
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={focusable ? 0 : -1}
            className={cx('segmented-cell', checked && 'is-active')}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

export function Checkbox({
  checked,
  onRow = false,
  className,
}: {
  checked: boolean
  onRow?: boolean | undefined
  className?: string | undefined
}) {
  return (
    <span
      aria-hidden="true"
      className={cx('checkbox', checked && 'is-checked', checked && onRow && 'on-row', className)}
    >
      {checked && <Icon name="check" size={16} strokeWidth={2.6} />}
    </span>
  )
}

export function CheckCircle({
  state,
  className,
}: {
  state: 'pending' | 'notfound'
  className?: string | undefined
}) {
  return (
    <span
      aria-hidden="true"
      className={cx('check-circle', state === 'notfound' && 'is-notfound', className)}
    >
      {state === 'notfound' && <Icon name="x" size={16} strokeWidth={2.6} />}
    </span>
  )
}

export function Chip({
  active,
  plus = false,
  className,
  type = 'button',
  children,
  ...rest
}: {
  active?: boolean | undefined
  plus?: boolean | undefined
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type={type}
      aria-pressed={plus ? undefined : active}
      {...rest}
      className={cx('chip', active && 'is-active', className)}
    >
      {plus && <Icon name="plus" size={16} strokeWidth={2.2} className="chip-plus" />}
      {children}
    </button>
  )
}

export function RadioCard({
  icon,
  title,
  description,
  selected,
  onSelect,
  className,
}: {
  icon: IconName
  title: string
  description: string
  selected: boolean
  onSelect(): void
  className?: string | undefined
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={cx('radio-card', selected && 'is-selected', className)}
      onClick={onSelect}
    >
      <span className="radio-card-icon">
        <Icon name={icon} size={22} />
      </span>
      <span className="radio-card-text">
        <span className="radio-card-title">{title}</span>
        <span className="radio-card-desc">{description}</span>
      </span>
      <span className="radio-card-dot" aria-hidden="true" />
    </button>
  )
}

/* ------------------------------------------------------- avatary a stitky */

function avatarInitial(name: string): string {
  const first = Array.from(name.trim())[0]
  return first ? first.toLocaleUpperCase('cs') : '?'
}

// Neutralni pozadi (card / step_hi / step) podle hashe jmena, at ma kazdy stale stejne.
function avatarTone(name: string): number {
  let hash = 0
  for (const ch of name) hash = (hash * 31 + (ch.codePointAt(0) ?? 0)) >>> 0
  return hash % 3
}

function avatarFontSize(size: number): number {
  return size <= 26 ? 10.5 : size <= 36 ? 14 : 16
}

export function Avatar({
  name,
  size = 40,
  ring = false,
  className,
}: {
  name: string
  size?: number | undefined
  ring?: boolean | undefined
  className?: string | undefined
}) {
  return (
    <span
      role="img"
      aria-label={name.trim() || '?'}
      className={cx('avatar', `avatar-bg-${avatarTone(name)}`, ring && 'has-ring', className)}
      style={{ width: size, height: size, fontSize: avatarFontSize(size) }}
    >
      {avatarInitial(name)}
    </span>
  )
}

export function AvatarStack({
  names,
  size = 26,
  max = 4,
  className,
}: {
  names: readonly string[]
  size?: number | undefined
  max?: number | undefined
  className?: string | undefined
}) {
  // Nejvic `max` kolecek celkem; pri preteceni posledni misto patri "+N".
  const limit = Math.max(1, Math.floor(max))
  const overflow = names.length > limit ? names.length - (limit - 1) : 0
  const shown = overflow > 0 ? names.slice(0, limit - 1) : names

  return (
    <span className={cx('avatar-stack', className)}>
      {shown.map((name, index) => (
        <Avatar key={`${index}-${name}`} name={name} size={size} ring />
      ))}
      {overflow > 0 && (
        <span
          role="img"
          aria-label={`a ${overflow} další`}
          className="avatar avatar-bg-2 has-ring"
          style={{ width: size, height: size, fontSize: avatarFontSize(size) }}
        >
          +{overflow}
        </span>
      )}
    </span>
  )
}

export function Badge({
  kind,
  children,
  className,
}: {
  kind: 'type' | 'planning' | 'shopping' | 'done'
  children: ReactNode
  className?: string | undefined
}) {
  return <span className={cx('badge', `badge-${kind}`, className)}>{children}</span>
}

/* ------------------------------------------------------- karty a nadpisy */

export function Card({
  padding = 'md',
  gap = 'md',
  className,
  children,
  ...rest
}: {
  padding?: 'none' | 'md' | undefined
  gap?: 'none' | 'sm' | 'md' | undefined
  children: ReactNode
} & HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      className={cx(
        'card',
        padding === 'none' && 'card-pad-none',
        gap !== 'md' && `card-gap-${gap}`,
        className,
      )}
    >
      {children}
    </div>
  )
}

export function Divider({ className }: { className?: string | undefined }) {
  return <hr className={cx('divider', className)} />
}

export function PageTitle({
  title,
  subtitle,
  size = 'section',
  action,
  className,
}: {
  title: string
  subtitle?: string | undefined
  size?: 'hero' | 'section' | undefined
  action?: ReactNode
  className?: string | undefined
}) {
  return (
    <div className={cx('page-title', size === 'hero' && 'is-hero', className)}>
      <div className="page-title-text">
        <h1 className="page-title-heading">{title}</h1>
        {subtitle && <span className="page-title-sub">{subtitle}</span>}
      </div>
      {hasContent(action) && <div className="page-title-action">{action}</div>}
    </div>
  )
}

export function Label({
  children,
  as: Tag = 'h2',
  className,
}: {
  children: ReactNode
  as?: 'h2' | 'h3' | 'span' | undefined
  className?: string | undefined
}) {
  return <Tag className={cx('label', Tag !== 'span' && 'label-block', className)}>{children}</Tag>
}

export function SectionHead({
  title,
  meta,
  className,
}: {
  title: string
  meta?: ReactNode
  className?: string | undefined
}) {
  return (
    <div className={cx('section-head', className)}>
      <Label>{title}</Label>
      {hasContent(meta) && <span className="section-head-meta">{meta}</span>}
    </div>
  )
}

export function Paragraph({
  children,
  size = 'body',
  tone = 'muted',
  align = 'start',
  className,
}: {
  children: ReactNode
  size?: 'body' | 'meta' | undefined
  tone?: Tone | undefined
  align?: 'start' | 'center' | undefined
  className?: string | undefined
}) {
  return (
    <p
      className={cx(
        'para',
        size === 'meta' && 'para-meta',
        `tone-${tone}`,
        align === 'center' && 'para-center',
        className,
      )}
    >
      {children}
    </p>
  )
}

export function TextCol({
  title,
  sub,
  subTone = 'muted',
  className,
}: {
  title: ReactNode
  sub?: ReactNode
  subTone?: Tone | undefined
  className?: string | undefined
}) {
  return (
    <span className={cx('text-col', className)}>
      <span className="text-col-title">{title}</span>
      {hasContent(sub) && <span className={cx('text-col-sub', `tone-${subTone}`)}>{sub}</span>}
    </span>
  )
}

/* ------------------------------------------------------------------ radky */

export function SelectRow({
  selected,
  children,
  onClick,
  as = 'div',
  pad = 'default',
  className,
  ...rest
}: {
  selected?: boolean | undefined
  children: ReactNode
  onClick?: (() => void) | undefined
  as?: 'div' | 'button' | undefined
  /** default = 8 8 8 12 (ovladac vpravo), even = 8 12 (sipka vpravo), grip = uchyt vlevo */
  pad?: 'default' | 'even' | 'grip' | undefined
} & Omit<HTMLAttributes<HTMLElement>, 'onClick' | 'children'>) {
  const classes = cx(
    'select-row',
    selected && 'is-selected',
    pad !== 'default' && `select-row-pad-${pad}`,
    className,
  )
  if (as === 'button') {
    return (
      <button type="button" aria-pressed={selected} {...rest} className={classes} onClick={onClick}>
        {children}
      </button>
    )
  }
  return (
    <div {...rest} className={classes} onClick={onClick}>
      {children}
    </div>
  )
}

export function ShopItem({
  name,
  sub,
  state,
  subTone,
  onToggle,
  toggleLabel,
  trailing,
  className,
  onContextMenu,
}: {
  onContextMenu?: ((event: MouseEvent<HTMLElement>) => void) | undefined
  name: string
  sub: ReactNode
  state: 'pending' | 'notfound'
  subTone?: 'muted' | 'error' | undefined
  onToggle(): void
  toggleLabel?: string | undefined
  trailing?: ReactNode
  className?: string | undefined
}) {
  const tone = subTone ?? (state === 'notfound' ? 'error' : 'muted')
  return (
    <div className={cx('shop-item', state === 'notfound' && 'is-notfound', className)} onContextMenu={onContextMenu}>
      <button type="button" className="shop-item-toggle" aria-pressed={false} onClick={onToggle}>
        <CheckCircle state={state} />
        <span className="shop-item-text">
          <span className="shop-item-name">{name}</span>
          {hasContent(sub) && <span className={cx('shop-item-sub', `tone-${tone}`)}>{sub}</span>}
          {toggleLabel && <span className="visually-hidden">{toggleLabel}</span>}
        </span>
      </button>
      {trailing}
    </div>
  )
}

export function BasketHead({
  label,
  open,
  onToggle,
  controls,
  className,
}: {
  label: string
  open: boolean
  onToggle(): void
  controls?: string | undefined
  className?: string | undefined
}) {
  return (
    <button
      type="button"
      className={cx('basket-head', className)}
      aria-expanded={open}
      aria-controls={controls}
      onClick={onToggle}
    >
      <span className="basket-head-label">{label}</span>
      <Icon name={open ? 'chevron_up' : 'chevron_down'} />
    </button>
  )
}

export function BasketRow({
  name,
  meta,
  strong = false,
  who,
  onClick,
  className,
}: {
  name: string
  meta: string
  strong?: boolean | undefined
  who?: string | null | undefined
  onClick?: (() => void) | undefined
  className?: string | undefined
}) {
  const inner = (
    <>
      <span className="basket-row-check" aria-hidden="true">
        <Icon name="check" size={14} strokeWidth={2.8} />
      </span>
      <span className="basket-row-name">{name}</span>
      {meta && <span className={cx('basket-row-meta', strong && 'is-strong')}>{meta}</span>}
      {who && <Avatar name={who} size={24} />}
    </>
  )
  if (onClick) {
    return (
      <button type="button" className={cx('basket-row', className)} onClick={onClick}>
        {inner}
      </button>
    )
  }
  return <div className={cx('basket-row', className)}>{inner}</div>
}

export function MenuItem({
  icon,
  label,
  tone = 'fg',
  iconTone,
  right,
  className,
  type = 'button',
  ...rest
}: {
  icon: IconName
  label: string
  tone?: 'fg' | 'error' | undefined
  iconTone?: Tone | undefined
  right?: ReactNode
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button {...rest} type={type} className={cx('menu-item', `tone-${tone}`, className)}>
      <Icon name={icon} size={24} className={iconTone ? `tone-${iconTone}` : undefined} />
      <span className="menu-item-label">{label}</span>
      {hasContent(right) && <span className="menu-item-right">{right}</span>}
    </button>
  )
}

/* ---------------------------------------------------- hlavicky a paticka */

export function TopBar({
  left,
  right,
  className,
}: {
  left: ReactNode
  right?: ReactNode
  className?: string | undefined
}) {
  return (
    <div className={cx('top-bar', className)}>
      <div className="top-bar-left">{left}</div>
      {right}
    </div>
  )
}

export function Tabs({
  children,
  label = 'Hlavní navigace',
  className,
}: {
  children: ReactNode
  label?: string | undefined
  className?: string | undefined
}) {
  return (
    <nav aria-label={label} className={cx('tabs', className)}>
      {children}
    </nav>
  )
}

export function GroupButton({
  name,
  onClick,
  className,
}: {
  name: string
  onClick(): void
  className?: string | undefined
}) {
  return (
    <button
      type="button"
      className={cx('group-button', className)}
      aria-haspopup="dialog"
      onClick={onClick}
    >
      <span className="group-button-name">{name}</span>
      <Icon name="chevron_down" size={18} />
    </button>
  )
}

export function ScreenHeader({
  title,
  subtitle,
  onBack,
  backLabel = 'Zpět',
  right,
  leftIcon = 'chevron_left',
  className,
}: {
  title: string
  subtitle?: string | undefined
  onBack?: (() => void) | undefined
  backLabel?: string | undefined
  right?: ReactNode
  leftIcon?: IconName | undefined
  className?: string | undefined
}) {
  return (
    <header className={cx('screen-header', !onBack && 'no-back', className)}>
      {onBack && <IconButton icon={leftIcon} label={backLabel} onClick={onBack} />}
      <div className="screen-header-text">
        <h1 className="screen-header-title">{title}</h1>
        {subtitle && <span className="screen-header-sub">{subtitle}</span>}
      </div>
      {hasContent(right) && <div className="screen-header-right">{right}</div>}
    </header>
  )
}

export function Footer({ children, className }: { children: ReactNode; className?: string | undefined }) {
  return <div className={cx('footer', className)}>{children}</div>
}

/* ------------------------------------------------------- stavy a hlaseni */

export function Banner({
  tone,
  children,
  action,
  className,
}: {
  tone: 'info' | 'warning' | 'error'
  children: ReactNode
  action?: ReactNode
  className?: string | undefined
}) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cx('banner', `banner-${tone}`, className)}
    >
      <div className="banner-body">{children}</div>
      {hasContent(action) && <div className="banner-action">{action}</div>}
    </div>
  )
}

export function Spinner({
  label = 'Načítám…',
  className,
}: {
  label?: string | undefined
  className?: string | undefined
}) {
  return (
    <span role="status" className={cx('spinner', className)}>
      <span className="spinner-ring" aria-hidden="true" />
      <span>{label}</span>
    </span>
  )
}

export function EmptyState({
  title,
  children,
  action,
  className,
}: {
  title: string
  children?: ReactNode
  action?: ReactNode
  className?: string | undefined
}) {
  return (
    <div className={cx('empty-state', className)}>
      <p className="empty-state-title">{title}</p>
      {hasContent(children) && <div className="empty-state-body">{children}</div>}
      {hasContent(action) && <div className="empty-state-action">{action}</div>}
    </div>
  )
}
